import React, { useRef, useState } from 'react';
import { Upload, Loader2, X } from 'lucide-react';
import { api } from '../api/client';
import { toast } from 'sonner';
import { Input } from './ui/input';
import { Button } from './ui/button';

/**
 * Reusable image upload + URL input. Calls onChange(url) when:
 *   - a file is uploaded → onChange('/api/media/file/<id>')
 *   - the user types a URL
 *
 * Props:
 *   - value: current url
 *   - onChange: fn(url)
 *   - testIdPrefix (optional)
 */
const MediaUpload = ({ value, onChange, testIdPrefix = 'media' }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await api.mediaUpload(file);
      onChange && onChange(res.url);
      toast.success('Uploaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-upload-wrap`}>
      <div className="flex gap-2 items-stretch">
        <Input
          value={value || ''}
          onChange={(e) => onChange && onChange(e.target.value)}
          placeholder="https://… or upload"
          className="bg-transparent border-zinc-800 flex-1"
          data-testid={`${testIdPrefix}-url`}
        />
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml,image/avif"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid={`${testIdPrefix}-file-input`}
        />
        <Button
          type="button"
          variant="outline"
          className="btn-outline-gold shrink-0"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          data-testid={`${testIdPrefix}-upload-btn`}
        >
          {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
          {uploading ? 'Uploading…' : 'Upload'}
        </Button>
        {value && (
          <Button type="button" variant="ghost" className="text-zinc-400 hover:text-rose-500 shrink-0" onClick={() => onChange && onChange('')}>
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
      {value && (
        <div className="border border-zinc-900 rounded p-2 bg-black">
          <img
            src={value}
            alt="preview"
            className="max-h-32 max-w-full object-contain mx-auto"
            data-testid={`${testIdPrefix}-preview`}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      )}
    </div>
  );
};

export default MediaUpload;
